import React from 'react';
import { useFlowFooterStore } from '@flow-studio/flow-observer-core';
import type {
  FlowFooterBadgeDescriptor,
  FlowFooterComponentProps,
  BadgeInteractionStrategy,
} from '@flow-studio/flow-observer-core';
import './FlowFooter.css';

// ── Badge renderers ───────────────────────────────────────────────────────────

function BadgeDot() {
  return <span className="flow-footer__dot" aria-hidden="true" />;
}

function renderBadgeContent(
  badge: FlowFooterBadgeDescriptor,
  translate: FlowFooterComponentProps['translate']
): React.ReactNode {
  const label = translate(badge.labelKey) || badge.fallbackLabel;

  const icon = badge.icon ? <span className="flow-footer__badge-icon" aria-hidden="true">{badge.icon}</span> : null;

  switch (badge.type) {
    case 'status':
      return (
        <>
          <BadgeDot />
          {icon}
          <span>{label}</span>
        </>
      );

    case 'state':
      return (
        <span className={`flow-footer__state--${badge.state}`}>
          {icon}
          {label}
        </span>
      );

    case 'text':
      return <span>{icon}{label}</span>;

    case 'count': {
      if (!badge.value) return null;
      return (
        <span>
          {icon}
          {label} {badge.value}
        </span>
      );
    }

    case 'progress': {
      const pct = Math.max(0, Math.min(badge.value ?? 0, 100));
      return (
        <>
          {icon}
          <span>{label}</span>
          <span className="flow-footer__progress-bar" aria-hidden="true">
            <span className="flow-footer__progress-fill" style={{ width: `${pct}%` }} />
          </span>
          <span>{pct}%</span>
        </>
      );
    }

    default:
      return <span>{icon}{label}</span>;
  }
}

function isInteractive(strategy: BadgeInteractionStrategy): boolean {
  return strategy.type !== 'none';
}

function getAriaLabel(
  badge: FlowFooterBadgeDescriptor,
  translate: FlowFooterComponentProps['translate']
): string {
  if (badge.badgeAriaKey) {
    return translate(badge.badgeAriaKey, badge.value !== undefined ? { count: badge.value } : {});
  }
  return translate(badge.labelKey) || badge.fallbackLabel;
}

// ── Main component ────────────────────────────────────────────────────────────

export const FlowFooter: React.FC<FlowFooterComponentProps> = ({
  currentScope,
  theme,
  onBadgeInteract,
  translate,
}) => {
  const badges = useFlowFooterStore((s) => s.badges);

  const visible = Object.values(badges).filter(
    (b) => b.visible && (b.visibleIn.includes('all') || b.visibleIn.includes(currentScope))
  );

  const leftBadges = visible
    .filter((b) => b.anchor === 'left')
    .sort((a, b) => a.order - b.order);

  const rightBadges = visible
    .filter((b) => b.anchor === 'right')
    .sort((a, b) => a.order - b.order);

  const renderBadge = (badge: FlowFooterBadgeDescriptor, idx: number, arr: FlowFooterBadgeDescriptor[]) => {
    // CountBadge with no value — hide
    if (badge.type === 'count' && !badge.value) return null;

    const content = renderBadgeContent(badge, translate);
    if (!content) return null;

    const interactive = isInteractive(badge.interaction);
    const ariaLabel = getAriaLabel(badge, translate);
    const tooltip = badge.tooltipKey
      ? translate(badge.tooltipKey) || badge.fallbackTooltip
      : badge.fallbackTooltip;

    // role="alert" carries implicit aria-live="assertive" (urgent, for errors).
    // role="status" carries implicit aria-live="polite" (non-disruptive, for all other states).
    const liveRole = badge.state === 'error' ? 'alert' : 'status';

    const badgeEl = interactive ? (
      <button
        key={badge.id}
        className={`flow-footer__badge flow-footer__badge--interactive flow-footer__badge--${badge.state}`}
        onClick={() => onBadgeInteract(badge.id, badge.interaction)}
        title={tooltip}
        aria-label={ariaLabel}
        tabIndex={0}
      >
        {content}
      </button>
    ) : (
      <span
        key={badge.id}
        role={liveRole}
        className={`flow-footer__badge flow-footer__badge--${badge.state}`}
        title={tooltip}
        aria-label={ariaLabel}
      >
        {content}
      </span>
    );

    return (
      <React.Fragment key={badge.id}>
        {badgeEl}
        {idx < arr.length - 1 && <span className="flow-footer__divider" aria-hidden="true" />}
      </React.Fragment>
    );
  };

  return (
    <footer
      className="flow-footer"
      data-theme={theme}
      role="status"
      aria-label={translate('flowFooter.ariaLabel') || 'Application status'}
    >
      <div
        className="flow-footer__group"
        role="group"
        aria-label={translate('flowFooter.group.left') || 'Workflow status'}
      >
        {leftBadges.map((b, i) => renderBadge(b, i, leftBadges))}
      </div>

      <div
        className="flow-footer__group"
        role="group"
        aria-label={translate('flowFooter.group.right') || 'System status'}
      >
        {rightBadges.map((b, i) => renderBadge(b, i, rightBadges))}
      </div>
    </footer>
  );
};
